import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { OfflineProvider, useOffline } from "@/context/OfflineContext";
import { CalendarDays, Users, LogOut, WifiOff, RefreshCw, Loader2, CloudUpload, Briefcase, CalendarOff, AlertTriangle, Link2, Check, X } from "lucide-react";
import icon from "@/assets/prydes-icon.png";
import BrandLogo from "@/components/BrandLogo";
import { PersonaToggle } from "@/components/PersonaToggle";
import { detectInApp } from "@/lib/inAppBrowser";
import { PageHelpLink } from "@/components/PageHelpLink";
import { NoContactBanner } from "@/components/NoContactBanner";

const NAV = [
  { to: "/worker", label: "My Roster", icon: CalendarDays, end: true },
  { to: "/worker/jobs", label: "Job Board", icon: Briefcase },
  { to: "/worker/leave", label: "Leave", icon: CalendarOff },
  { to: "/worker/participants", label: "Participants", icon: Users },
];

function OfflineBar() {
  const { online, pending, syncing, syncNow } = useOffline();
  if (online && pending === 0) return null;
  return (
    <div data-testid="worker-offline-bar"
      className={`px-4 py-1.5 flex items-center justify-between text-[11px] font-medium ${online ? "bg-amber-500/15 text-amber-200" : "bg-red-500/20 text-red-200"}`}>
      <div className="flex items-center gap-1.5">
        {online
          ? <><CloudUpload className="h-3.5 w-3.5" /> {pending} change{pending > 1 ? "s" : ""} waiting to sync</>
          : <><WifiOff className="h-3.5 w-3.5" /> Offline — changes are saved on this device{pending ? ` (${pending} pending)` : ""}</>}
      </div>
      {online && pending > 0 && (
        <button onClick={() => syncNow()} disabled={syncing} data-testid="worker-sync-now"
          className="flex items-center gap-1 font-semibold rounded-full px-2.5 py-0.5 bg-white/15 hover:bg-white/25 transition-colors">
          {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Sync now
        </button>
      )}
    </div>
  );
}

function InAppBrowserBanner() {
  const [info] = useState(detectInApp);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("iab-dismissed") === "1");
  const [copied, setCopied] = useState(false);
  if (!info.inApp || dismissed) return null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard may be blocked in the webview too */ }
  };
  const dismiss = () => { sessionStorage.setItem("iab-dismissed", "1"); setDismissed(true); };

  return (
    <div data-testid="inapp-browser-banner" className="px-4 py-2.5 bg-amber-400/15 border-b border-amber-400/25 text-amber-100">
      <div className="flex items-start gap-2 max-w-lg mx-auto">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-300" />
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-semibold text-amber-50">Open in Safari or Chrome to clock in</div>
          <div className="text-[11px] leading-snug text-amber-200/90 mt-0.5">
            You're viewing this inside {info.app}. Location often doesn't work in in-app browsers, so clocking in/out may fail even with your location turned on. Tap the <span className="font-semibold">•••</span> or share menu and choose "Open in Browser", or copy the link and paste it into Safari/Chrome.
          </div>
          <div className="flex items-center gap-2 mt-2">
            <button onClick={copyLink} data-testid="inapp-copy-link"
              className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 bg-amber-300/20 hover:bg-amber-300/30 text-amber-50 transition-colors">
              {copied ? <><Check className="h-3 w-3" /> Copied</> : <><Link2 className="h-3 w-3" /> Copy link</>}
            </button>
            <button onClick={dismiss} data-testid="inapp-dismiss"
              className="inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2.5 py-1 text-amber-200/80 hover:text-amber-100 transition-colors">
              <X className="h-3 w-3" /> Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function WorkerShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <OfflineProvider>
    <div className="min-h-screen flex flex-col font-body" style={{ backgroundColor: "#0B1220" }} data-testid="worker-shell">
      <NoContactBanner />
      {/* Top bar */}
      <header className="h-14 px-4 flex items-center justify-between sticky top-0 z-30 border-b border-white/10"
        style={{ background: "linear-gradient(90deg,#a32f42,#bf3b4f)" }}>
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-white flex items-center justify-center shadow-sm">
            <BrandLogo variant="icon" fallback={icon} className="h-6 w-6 object-contain" />
          </div>
          <div className="leading-tight">
            <div className="text-[13px] font-bold text-white tracking-tight font-brand">Prydes Support</div>
            <div className="text-[10px] text-white/70 -mt-0.5">Worker App</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PersonaToggle variant="dark" compact />
          <PageHelpLink basePath="/worker/help" iconClassName="h-4 w-4 text-white/90"
            className="h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors" />
          <button onClick={logout} data-testid="worker-logout"
            className="flex items-center gap-2 rounded-full pl-2 pr-3 py-1 bg-white/15 hover:bg-white/25 transition-colors">
            {user?.photo
              ? <img src={user.photo} alt={user?.name || "You"} className="h-7 w-7 rounded-full object-cover" data-testid="worker-avatar-photo" />
              : <div className="h-7 w-7 rounded-full text-white flex items-center justify-center text-[11px] font-semibold" style={{ backgroundColor: "#3f9963" }}>{initials}</div>}
            <LogOut className="h-3.5 w-3.5 text-white/90" />
          </button>
        </div>
      </header>

      <OfflineBar />
      <InAppBrowserBanner />

      {/* Content */}
      <main className="flex-1 min-w-0 pb-20 max-w-lg w-full mx-auto">
        <Outlet />
      </main>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-30 border-t border-white/10 bg-[#0F1B33]/95 backdrop-blur">
        <div className="max-w-lg mx-auto grid grid-cols-4">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              data-testid={`worker-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors"
              style={({ isActive }) => ({ color: isActive ? "#00c86e" : "#94a3b8" })}>
              <n.icon className="h-5 w-5" />
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
    </OfflineProvider>
  );
}
